import { todayStr } from "../../lib/date";
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { createIncidentReport, uploadReportAttachment } from "../../lib/api";
import { sortByFirst, buildFirstNameMap } from "../../lib/names";
import AttachmentPicker from "./AttachmentPicker";

const INCIDENT_TYPES = [
  "Not Ready / Refusal to participate",
  "Disruption of learning",
  "Left Building",
  "Threatening language-verbal",
  "Threatening language-written",
  "Property damage",
  "Physical Altercation",
  "Sexual Harassment",
  "Threats of self-harm",
  "Self-harm",
  "Disclosure of abuse or neglect",
  "Home Life Issue",
  "Contraband Found",
  "Substance Abuse",
];

export default function SubmitReportTab({ students, staffList, user }) {
  const [incidentForm, setIncidentForm] = useState({
    student_id: "",
    date: todayStr(),
    time: "",
    type: "clinical",
    incident_type: "",
    description: "",
    action_taken: "",
    follow_up_needed: false,
    witnesses: "",
    victims: [],
    aggressors: [],
    witnesses_students: [],
    other_involved: [],
    infractions: [],
    visibility: ["Admin"],
  });

  const [pendingFiles, setPendingFiles] = useState([]);

  const uploadPending = async (reportType, reportId) => {
    for (const pf of pendingFiles) {
      try { await uploadReportAttachment(reportType, reportId, pf.file, pf.description); }
      catch { toast.error(`Failed to attach ${pf.file.name}`); }
    }
  };

  const [accidentSubject, setAccidentSubject] = useState("student");
  const [accidentForm, setAccidentForm] = useState({
    subject_id: "",
    subject_name: "",
    date: todayStr(),
    time: "",
    place_of_accident: "",
    describe_incident: "",
    describe_injury: "",
    witness_name: "",
    medical_response: "first_aid",
    follow_up: "",
  });

  const toggleVisibility = (role) => {
    setIncidentForm((prev) => ({
      ...prev,
      visibility: prev.visibility.includes(role)
        ? prev.visibility.filter((r) => r !== role)
        : [...prev.visibility, role],
    }));
  };

  const handleSubmitIncident = async (e) => {
    e.preventDefault();
    if (!incidentForm.student_id || !incidentForm.description) {
      toast.error("Please fill in required fields");
      return;
    }
    if (incidentForm.type === "incident" && !incidentForm.incident_type) {
      toast.error("Please select an incident type");
      return;
    }
    try {
      const res = await createIncidentReport({ ...incidentForm, reporter_id: user.id });
      const newId = res?.data?.id;
      if (newId && pendingFiles.length) await uploadPending(incidentForm.type, newId);
      toast.success("Report submitted");
      setIncidentForm({
        student_id: "", date: todayStr(), time: "",
        type: "clinical", incident_type: "", description: "", action_taken: "",
        follow_up_needed: false, witnesses: "", victims: [], aggressors: [], witnesses_students: [],
        other_involved: [], infractions: [], visibility: ["Admin"],
      });
      setPendingFiles([]);
    } catch { toast.error("Failed to submit report"); }
  };

  const handleSubmitAccidentReport = async (e) => {
    e.preventDefault();
    if (!accidentForm.subject_id || !accidentForm.describe_incident || !accidentForm.describe_injury) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const reportData = {
        subject_type: accidentSubject, subject_id: accidentForm.subject_id,
        subject_name: accidentForm.subject_name, date: accidentForm.date,
        time: accidentForm.time, place_of_accident: accidentForm.place_of_accident,
        describe_incident: accidentForm.describe_incident, describe_injury: accidentForm.describe_injury,
        witness_name: accidentForm.witness_name, medical_response: accidentForm.medical_response,
        follow_up: accidentForm.follow_up, reporter_id: user.id,
      };
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accident-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(reportData),
      });
      if (!response.ok) throw new Error('Failed to submit report');
      const created = await response.json();
      const newId = created?.id;
      if (newId && pendingFiles.length) {
        await uploadPending(accidentSubject === 'staff' ? 'accident_staff' : 'accident_student', newId);
      }
      toast.success(`${accidentSubject === 'staff' ? 'Staff' : 'Student'} accident report submitted`);
      setAccidentForm({
        subject_id: "", subject_name: "", date: todayStr(),
        time: "", place_of_accident: "", describe_incident: "", describe_injury: "",
        witness_name: "", medical_response: "first_aid", follow_up: "",
      });
      setPendingFiles([]);
      setAccidentSubject("student");
    } catch { toast.error("Failed to submit accident report"); }
  };

  const sortedStudents = sortByFirst(students);
  const studentNameMap = buildFirstNameMap(students);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Incident / Clinical Report</CardTitle>
        <CardDescription>Record incidents, accidents, or clinical notes</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 mb-6">
          <Label>Report Type *</Label>
          <Select value={incidentForm.type} onValueChange={(v) => setIncidentForm((p) => ({ ...p, type: v }))}>
            <SelectTrigger data-testid="incident-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="clinical">Clinical Report</SelectItem>
              <SelectItem value="incident">Incident Report</SelectItem>
              <SelectItem value="accident">Accident Report</SelectItem>
              <SelectItem value="general_note">General Note</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {incidentForm.type === "accident" ? (
          <div className="space-y-6">
            <div className="p-4 bg-[#00305E]/5 rounded-lg">
              <Label className="text-base font-semibold mb-3 block">Who is this accident report for?</Label>
              <div className="flex gap-4">
                {[{ val: "student", label: "Student", desc: "Report a student injury", color: "#00305E" },
                  { val: "staff", label: "Staff Member", desc: "Report a staff injury", color: "#A62E1D" }].map(opt => (
                  <label key={opt.val} className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all ${accidentSubject === opt.val ? `border-[${opt.color}] bg-[${opt.color}]/10` : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="accidentSubject" value={opt.val} checked={accidentSubject === opt.val}
                      onChange={() => { setAccidentSubject(opt.val); setAccidentForm(p => ({ ...p, subject_id: '', subject_name: '' })); }} className="sr-only" />
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${accidentSubject === opt.val ? `border-[${opt.color}]` : 'border-gray-400'}`}>
                        {accidentSubject === opt.val && <div className="w-3 h-3 rounded-full" style={{ background: opt.color }}></div>}
                      </div>
                      <div><p className="font-medium">{opt.label}</p><p className="text-sm text-muted-foreground">{opt.desc}</p></div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <form onSubmit={handleSubmitAccidentReport} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{accidentSubject === 'student' ? 'Student' : 'Staff Member'} *</Label>
                  <Select value={accidentForm.subject_id} onValueChange={(v) => {
                    const list = accidentSubject === 'student' ? students : staffList;
                    const subject = list.find(s => s.id === v);
                    setAccidentForm(p => ({ ...p, subject_id: v, subject_name: subject ? (subject.first_name ? `${subject.first_name} ${subject.last_name}` : subject.name) : '' }));
                  }}>
                    <SelectTrigger data-testid="accident-subject-select"><SelectValue placeholder={`Select ${accidentSubject}`} /></SelectTrigger>
                    <SelectContent>
                      {accidentSubject === 'student' ? sortedStudents.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{studentNameMap[s.id]}</SelectItem>
                      )) : staffList.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Place of Accident *</Label>
                  <Input value={accidentForm.place_of_accident} onChange={(e) => setAccidentForm(p => ({ ...p, place_of_accident: e.target.value }))} placeholder="e.g., Classroom, Playground, Gym" required data-testid="accident-place" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Date of Accident *</Label><Input type="date" value={accidentForm.date} onChange={(e) => setAccidentForm(p => ({ ...p, date: e.target.value }))} required data-testid="accident-date" /></div>
                <div className="space-y-2"><Label>Time of Incident *</Label><Input type="time" value={accidentForm.time} onChange={(e) => setAccidentForm(p => ({ ...p, time: e.target.value }))} required data-testid="accident-time" /></div>
              </div>
              <div className="space-y-2"><Label>Describe Incident *</Label><Textarea placeholder="Describe what happened during the incident..." value={accidentForm.describe_incident} onChange={(e) => setAccidentForm(p => ({ ...p, describe_incident: e.target.value }))} rows={4} required data-testid="accident-describe-incident" /></div>
              <div className="space-y-2"><Label>Describe Injury *</Label><Textarea placeholder="Describe the injury sustained..." value={accidentForm.describe_injury} onChange={(e) => setAccidentForm(p => ({ ...p, describe_injury: e.target.value }))} rows={3} required data-testid="accident-describe-injury" /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Witness Name (Staff) *</Label><Input value={accidentForm.witness_name} onChange={(e) => setAccidentForm(p => ({ ...p, witness_name: e.target.value }))} placeholder="Name of staff witness" required data-testid="accident-witness" /></div>
                <div className="space-y-2">
                  <Label>Medical Response *</Label>
                  <Select value={accidentForm.medical_response} onValueChange={(v) => setAccidentForm(p => ({ ...p, medical_response: v }))}>
                    <SelectTrigger data-testid="accident-medical-response"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="first_aid">First Aid</SelectItem><SelectItem value="ambulance">Ambulance</SelectItem><SelectItem value="na">N/A</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Follow-up (If Applicable)</Label><Textarea placeholder="Any follow-up actions or notes..." value={accidentForm.follow_up} onChange={(e) => setAccidentForm(p => ({ ...p, follow_up: e.target.value }))} rows={2} data-testid="accident-followup" /></div>
              <div className="space-y-2"><Label>Attachments</Label><AttachmentPicker files={pendingFiles} onChange={setPendingFiles} /></div>
              <Button type="submit" className={`${accidentSubject === 'staff' ? 'bg-[#A62E1D] hover:bg-[#A62E1D]/90' : 'bg-[#00305E] hover:bg-[#00305E]/90'}`} data-testid="submit-accident-btn">
                Submit {accidentSubject === 'staff' ? 'Staff' : 'Student'} Accident Report
              </Button>
            </form>
          </div>
        ) : incidentForm.type === "incident" ? (
          <form onSubmit={handleSubmitIncident} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Student *</Label>
                <Select value={incidentForm.student_id} onValueChange={(v) => setIncidentForm((p) => ({ ...p, student_id: v }))}>
                  <SelectTrigger data-testid="incident-primary-student-select"><SelectValue placeholder="Select primary student" /></SelectTrigger>
                  <SelectContent>{sortedStudents.map((s) => (<SelectItem key={s.id} value={s.id}>{studentNameMap[s.id]}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Primary Type of Incident *</Label>
                <Select value={incidentForm.incident_type} onValueChange={(v) => setIncidentForm((p) => ({ ...p, incident_type: v }))}>
                  <SelectTrigger data-testid="incident-category-select"><SelectValue placeholder="Select incident type" /></SelectTrigger>
                  <SelectContent>{INCIDENT_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date *</Label><Input type="date" value={incidentForm.date} onChange={(e) => setIncidentForm((p) => ({ ...p, date: e.target.value }))} data-testid="incident-date" /></div>
              <div className="space-y-2"><Label>Time *</Label><Input type="time" value={incidentForm.time} onChange={(e) => setIncidentForm((p) => ({ ...p, time: e.target.value }))} data-testid="incident-time" /></div>
            </div>
            <div className="space-y-2"><Label>Description *</Label><Textarea placeholder="Describe the incident in detail..." value={incidentForm.description} onChange={(e) => setIncidentForm((p) => ({ ...p, description: e.target.value }))} rows={4} required data-testid="incident-description" /></div>
            <div className="space-y-2"><Label>Action Taken</Label><Textarea placeholder="Describe any actions taken..." value={incidentForm.action_taken} onChange={(e) => setIncidentForm((p) => ({ ...p, action_taken: e.target.value }))} rows={2} data-testid="incident-action" /></div>
            <div className="space-y-2 p-4 border rounded-lg bg-red-50/30">
              <Label className="font-semibold">Victims</Label>
              <p className="text-xs text-muted-foreground mb-2">Select students who were victims in this incident</p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {sortedStudents.map((s) => (
                  <label key={s.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs cursor-pointer border transition-colors ${incidentForm.victims.includes(s.id) ? 'bg-red-100 border-red-300 text-red-800' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <input type="checkbox" className="sr-only" checked={incidentForm.victims.includes(s.id)}
                      onChange={() => setIncidentForm(p => ({ ...p, victims: p.victims.includes(s.id) ? p.victims.filter(id => id !== s.id) : [...p.victims, s.id] }))} />
                    {studentNameMap[s.id]}
                  </label>
                ))}
              </div>
              {incidentForm.victims.length > 0 && <p className="text-xs text-red-700 mt-1">{incidentForm.victims.length} victim(s) selected</p>}
            </div>
            <div className="space-y-2 p-4 border rounded-lg bg-orange-50/40">
              <Label className="font-semibold">Student(s) Involved / Aggressor(s)</Label>
              <p className="text-xs text-muted-foreground mb-2">Select students listed as involved / the aggressor in this incident (used for bullying/harassment pattern detection)</p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto" data-testid="incident-aggressors">
                {sortedStudents.map((s) => (
                  <label key={s.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs cursor-pointer border transition-colors ${incidentForm.aggressors.includes(s.id) ? 'bg-orange-100 border-orange-300 text-orange-800' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <input type="checkbox" className="sr-only" checked={incidentForm.aggressors.includes(s.id)}
                      onChange={() => setIncidentForm(p => ({ ...p, aggressors: p.aggressors.includes(s.id) ? p.aggressors.filter(id => id !== s.id) : [...p.aggressors, s.id] }))} />
                    {studentNameMap[s.id]}
                  </label>
                ))}
              </div>
              {incidentForm.aggressors.length > 0 && <p className="text-xs text-orange-700 mt-1">{incidentForm.aggressors.length} student(s) selected</p>}
            </div>
            <div className="space-y-2 p-4 border rounded-lg bg-blue-50/30">
              <Label className="font-semibold">Student Witnesses</Label>
              <p className="text-xs text-muted-foreground mb-2">Select students who witnessed the incident</p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {sortedStudents.map((s) => (
                  <label key={s.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs cursor-pointer border transition-colors ${incidentForm.witnesses_students.includes(s.id) ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <input type="checkbox" className="sr-only" checked={incidentForm.witnesses_students.includes(s.id)}
                      onChange={() => setIncidentForm(p => ({ ...p, witnesses_students: p.witnesses_students.includes(s.id) ? p.witnesses_students.filter(id => id !== s.id) : [...p.witnesses_students, s.id] }))} />
                    {studentNameMap[s.id]}
                  </label>
                ))}
              </div>
              {incidentForm.witnesses_students.length > 0 && <p className="text-xs text-blue-700 mt-1">{incidentForm.witnesses_students.length} witness(es) selected</p>}
            </div>
            <div className="space-y-2"><Label>Staff Witness</Label><Input placeholder="Names of staff witnesses" value={incidentForm.witnesses} onChange={(e) => setIncidentForm((p) => ({ ...p, witnesses: e.target.value }))} /></div>
            <div className="space-y-3 p-4 border rounded-lg bg-amber-50/30">
              <div className="flex items-center justify-between">
                <div><Label className="font-semibold">Student Infractions</Label><p className="text-xs text-muted-foreground">Add infractions for involved students</p></div>
                <Button type="button" variant="outline" size="sm" data-testid="add-infraction-btn"
                  onClick={() => setIncidentForm(p => ({ ...p, infractions: [...p.infractions, { student_id: "", infraction: "", notes: "" }] }))}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Infraction
                </Button>
              </div>
              {incidentForm.infractions.map((inf, idx) => (
                <div key={idx} className="p-3 bg-white border rounded-lg space-y-2" data-testid={`infraction-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-amber-700">Infraction #{idx + 1}</span>
                    <button type="button" onClick={() => setIncidentForm(p => ({ ...p, infractions: p.infractions.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Select value={inf.student_id} onValueChange={(v) => { const updated = [...incidentForm.infractions]; updated[idx] = { ...updated[idx], student_id: v }; setIncidentForm(p => ({ ...p, infractions: updated })); }}>
                      <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>{sortedStudents.map((s) => (<SelectItem key={s.id} value={s.id}>{studentNameMap[s.id]}</SelectItem>))}</SelectContent>
                    </Select>
                    <Select value={inf.infraction} onValueChange={(v) => { const updated = [...incidentForm.infractions]; updated[idx] = { ...updated[idx], infraction: v }; setIncidentForm(p => ({ ...p, infractions: updated })); }}>
                      <SelectTrigger><SelectValue placeholder="Select infraction type" /></SelectTrigger>
                      <SelectContent>{INCIDENT_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <Input placeholder="Additional notes for this infraction" value={inf.notes || ""} onChange={(e) => { const updated = [...incidentForm.infractions]; updated[idx] = { ...updated[idx], notes: e.target.value }; setIncidentForm(p => ({ ...p, infractions: updated })); }} className="text-sm" />
                </div>
              ))}
              {incidentForm.infractions.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No infractions added. Click "Add Infraction" to add one.</p>}
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="followup-incident" checked={incidentForm.follow_up_needed} onCheckedChange={(c) => setIncidentForm((p) => ({ ...p, follow_up_needed: c }))} />
              <Label htmlFor="followup-incident" className="cursor-pointer">Follow-up Needed</Label>
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex gap-4">
                {["Admin", "Clinician", "All Staff"].map((role) => (
                  <div key={role} className="flex items-center space-x-2">
                    <Checkbox id={`vis-inc-${role}`} checked={incidentForm.visibility.includes(role)} onCheckedChange={() => toggleVisibility(role)} />
                    <Label htmlFor={`vis-inc-${role}`} className="cursor-pointer">{role}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>Attachments</Label><AttachmentPicker files={pendingFiles} onChange={setPendingFiles} /></div>
            <Button type="submit" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="submit-incident-btn">Submit Incident Report</Button>
          </form>
        ) : (
          <form onSubmit={handleSubmitIncident} className="space-y-4">
            <div className="space-y-2">
              <Label>Student *</Label>
              <Select value={incidentForm.student_id} onValueChange={(v) => setIncidentForm((p) => ({ ...p, student_id: v }))}>
                <SelectTrigger data-testid="incident-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{sortedStudents.map((s) => (<SelectItem key={s.id} value={s.id}>{studentNameMap[s.id]}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={incidentForm.date} onChange={(e) => setIncidentForm((p) => ({ ...p, date: e.target.value }))} data-testid="incident-date" /></div>
              <div className="space-y-2"><Label>Time</Label><Input type="time" value={incidentForm.time} onChange={(e) => setIncidentForm((p) => ({ ...p, time: e.target.value }))} data-testid="incident-time" /></div>
            </div>
            <div className="space-y-2"><Label>Description *</Label><Textarea placeholder="Describe the incident or observation..." value={incidentForm.description} onChange={(e) => setIncidentForm((p) => ({ ...p, description: e.target.value }))} rows={4} required data-testid="incident-description" /></div>
            <div className="space-y-2"><Label>Action Taken</Label><Textarea placeholder="Describe any actions taken..." value={incidentForm.action_taken} onChange={(e) => setIncidentForm((p) => ({ ...p, action_taken: e.target.value }))} rows={2} data-testid="incident-action" /></div>
            <div className="space-y-2"><Label>Witnesses</Label><Input placeholder="Names of any witnesses" value={incidentForm.witnesses} onChange={(e) => setIncidentForm((p) => ({ ...p, witnesses: e.target.value }))} /></div>
            <div className="flex items-center space-x-2">
              <Checkbox id="followup" checked={incidentForm.follow_up_needed} onCheckedChange={(c) => setIncidentForm((p) => ({ ...p, follow_up_needed: c }))} />
              <Label htmlFor="followup" className="cursor-pointer">Follow-up Needed</Label>
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex gap-4">
                {["Admin", "Clinician", "All Staff"].map((role) => (
                  <div key={role} className="flex items-center space-x-2">
                    <Checkbox id={`vis-${role}`} checked={incidentForm.visibility.includes(role)} onCheckedChange={() => toggleVisibility(role)} />
                    <Label htmlFor={`vis-${role}`} className="cursor-pointer">{role}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>Attachments</Label><AttachmentPicker files={pendingFiles} onChange={setPendingFiles} /></div>
            <Button type="submit" className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="submit-incident-btn">Submit Report</Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
